const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-gap-xs: var(--ctl-space-2, 8px);
    --ctl-gap-sm: var(--ctl-space-3, 12px);
    --ctl-gap-md: var(--ctl-space-4, 16px);
    --ctl-gap-lg: var(--ctl-space-5, 24px);
    --ctl-gap-xl: var(--ctl-space-7, 48px);

    display: grid;
    grid-template-columns: var(--_columns, repeat(auto-fit, minmax(var(--_min-col, 250px), 1fr)));
    gap: var(--_gap, var(--ctl-gap-md));
    box-sizing: border-box;
  }

  /* gap */
  :host([gap="xs"]) { --_gap: var(--ctl-gap-xs); }
  :host([gap="sm"]) { --_gap: var(--ctl-gap-sm); }
  :host([gap="md"]) { --_gap: var(--ctl-gap-md); }
  :host([gap="lg"]) { --_gap: var(--ctl-gap-lg); }
  :host([gap="xl"]) { --_gap: var(--ctl-gap-xl); }

  /* numeric column counts */
  :host([columns="1"])  { --_columns: repeat(1,  1fr); }
  :host([columns="2"])  { --_columns: repeat(2,  1fr); }
  :host([columns="3"])  { --_columns: repeat(3,  1fr); }
  :host([columns="4"])  { --_columns: repeat(4,  1fr); }
  :host([columns="5"])  { --_columns: repeat(5,  1fr); }
  :host([columns="6"])  { --_columns: repeat(6,  1fr); }
  :host([columns="7"])  { --_columns: repeat(7,  1fr); }
  :host([columns="8"])  { --_columns: repeat(8,  1fr); }
  :host([columns="9"])  { --_columns: repeat(9,  1fr); }
  :host([columns="10"]) { --_columns: repeat(10, 1fr); }
  :host([columns="11"]) { --_columns: repeat(11, 1fr); }
  :host([columns="12"]) { --_columns: repeat(12, 1fr); }

  /* responsive keyword columns */
  :host([columns="auto-fit"])  { --_columns: repeat(auto-fit,  minmax(var(--_min-col, 250px), 1fr)); }
  :host([columns="auto-fill"]) { --_columns: repeat(auto-fill, minmax(var(--_min-col, 250px), 1fr)); }
</style>

<slot></slot>
`;

class CtlGrid extends HTMLElement {
  static get observedAttributes() {
    return ['columns', 'cols', 'gap', 'min-col'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    // Mirror cols → columns alias
    if (this.hasAttribute('cols') && !this.hasAttribute('columns')) {
      this.setAttribute('columns', this.getAttribute('cols'));
    }
    if (!this.hasAttribute('columns')) this.setAttribute('columns', 'auto-fit');
    if (!this.hasAttribute('gap')) this.setAttribute('gap', 'md');
    // Only apply --_min-col when the attribute is explicitly set
    if (this.hasAttribute('min-col')) {
      this.style.setProperty('--_min-col', this.getAttribute('min-col'));
    }
    // Apply inline columns for values outside the enumerated CSS range
    this._applyColumnsStyle(this.getAttribute('columns'));
  }

  _applyColumnsStyle(value) {
    const n = parseInt(value, 10);
    if (value !== null && !isNaN(n) && n > 12) {
      this.style.setProperty('--_columns', `repeat(${n}, 1fr)`);
    } else {
      this.style.removeProperty('--_columns');
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'min-col') {
      if (newValue !== null) {
        this.style.setProperty('--_min-col', newValue);
      } else {
        this.style.removeProperty('--_min-col');
      }
    } else if (name === 'cols') {
      // Mirror cols to columns (guard against redundant setAttribute to avoid loops)
      if (newValue !== null && this.getAttribute('columns') !== newValue) {
        this.setAttribute('columns', newValue);
      }
    } else if (name === 'columns') {
      this._applyColumnsStyle(newValue);
    }
    // gap handled by CSS :host([gap]) selectors
  }

  get columns() { return this.getAttribute('columns') || 'auto-fit'; }
  set columns(v) { this.setAttribute('columns', String(v || 'auto-fit')); }

  // cols is a convenience alias; setter writes through to the canonical columns attribute
  get cols() { return this.getAttribute('cols') || this.getAttribute('columns') || 'auto-fit'; }
  set cols(v) { this.setAttribute('columns', String(v || 'auto-fit')); }

  get gap() { return this.getAttribute('gap') || 'md'; }
  set gap(v) { this.setAttribute('gap', String(v || 'md')); }

  get minCol() { return this.getAttribute('min-col') || '250px'; }
  set minCol(v) { this.setAttribute('min-col', String(v || '250px')); }
}

customElements.define("ctl-grid", CtlGrid);

export { CtlGrid };
